import useCurrentAccount from "services/useCurrentAccount"
import { useModalStore } from "services/useModal"
import { useAccountUsers } from "queries/accountUsers"
import {
  Redirect, Route, Switch, useHistory,
} from "react-router-dom"
import React, { useState } from "react"
import {
  User, Account as DioboxAccount, AccountUser,
} from "sharedTypes"
import {
  accountPath,
  accountPlanPath,
  accountTeamPath, completeRegistrationPath,
  eventsFuturePath, eventsPastPath,
  eventsPath, leadRetrievalRegisterPath, upgradePath,
} from "utilities/routes"
import Account from "pages/Account"
import Plan from "pages/Account/Plan"
import TeamMembers from "pages/Account/TeamMembers"
import UpgradePlan from "modals/UpgradePlan"
import EventsIndex from "pages/EventsIndex"
import EventShow from "pages/EventShow"
import DownloadApp from "pages/DownloadApp"
import { PusherProvider } from "@harelpls/use-pusher"
import CompleteRegistration from "pages/Unauthenticated/CompleteRegistration"
import LeadRetrievalRegisterWrapper
  from "pages/Unauthenticated/LeadRetrieval/Register/LeadRetrievalRegisterWrapper"
import LeadRetrievalInviteRedeemWrapper
  from "pages/Unauthenticated/LeadRetrieval/Register/InviteRedeem/LeadRetrievalInviteRedeemWrapper"
import DesktopView from "../layout/DesktopView"
import PusherWrapper from "../PusherWrapper"
import ProtectedRoute from "../ProtectedRoute"
import Navigation from "../Navigation"
import ErrorView from "../ErrorView"
import RouteErrorBoundary from "../RouteErrorBoundary"
import Spinner from "../elements/Spinner"
import Modals from "./Modals"
import AccountErrors from "./AccountErrors"
import { useConfirmEmailAuthedGate } from "./ConfirmEmailAuthedGate"

type Props = {
  currentUser: User
  onClickForceDesktopView: () => void
  view: "mobile" | "desktop"
}

const Authenticated = ({ currentUser, onClickForceDesktopView, view }: Props) => {
  const currentAccount = useCurrentAccount()
  const currentModal = useModalStore((s) => s.modal)
  const { data: accountUsers, isLoading: accountUsersIsloading } = useAccountUsers(
    currentAccount ? currentAccount.disabled : true,
  )
  const [
    closedPastDueNotificationAccountIDs,
    setClosedPastDueNotificationAccountIDs,
  ] = useState<string[]>([])

  const confirmEmailAuthedGateResult = useConfirmEmailAuthedGate(currentUser)
  if (confirmEmailAuthedGateResult) return confirmEmailAuthedGateResult

  if (accountUsersIsloading) return <Spinner />
  if (!accountUsers) return null

  return (
    // @ts-ignore
    <PusherProvider
      clientKey={import.meta.env.VITE_PUSHER_KEY!}
      cluster={import.meta.env.VITE_PUSHER_CLUSTER!}
    >
      <AccountErrors
        currentAccount={currentAccount}
        currentUser={currentUser}
        setClosedPastDueNotificationAccountIDs={setClosedPastDueNotificationAccountIDs}
        closedPastDueNotificationAccountIDs={closedPastDueNotificationAccountIDs}
      />
      {currentModal?.name && (
        <Modals
          currentAccount={currentAccount}
          currentUser={currentUser}
          currentModal={currentModal}
          teamMembers={accountUsers}
        />
      )}
      {currentAccount && view === "desktop" ? (
        <DesktopView>
          <DesktopRoutes
            currentAccount={currentAccount}
            accountUsers={accountUsers}
            setClosedPastDueNotificationAccountIDs={setClosedPastDueNotificationAccountIDs}
            currentUser={currentUser}
          />
        </DesktopView>
      ) : (
        <Switch>
          {completeRegistrationRoute()}
          <Route>
            <DownloadApp onClickForceDesktopView={onClickForceDesktopView} />
          </Route>
        </Switch>
      )}
    </PusherProvider>
  )
}

const completeRegistrationRoute = () => (
  <Route path={completeRegistrationPath()} exact>
    <CompleteRegistration />
  </Route>
)

type AuthenticatedRoutesProps = {
  currentAccount: DioboxAccount,
  currentUser: User,
  setClosedPastDueNotificationAccountIDs: (ids: (prevIDs) => any) => void
  accountUsers: AccountUser[]
}

const DesktopRoutes = ({
  currentAccount, currentUser, accountUsers, setClosedPastDueNotificationAccountIDs,
}: AuthenticatedRoutesProps) => {
  const history = useHistory()
  const { admin } = currentAccount

  const handleClickResolve = () => {
    setClosedPastDueNotificationAccountIDs(
      (prevIDs) => prevIDs.filter((id) => id !== currentAccount.id),
    )
  }

  return (
    <Switch>
      {completeRegistrationRoute()}
      <Route path="/error" exact>
        <ErrorView clearError={() => {
        }}
        />
      </Route>
      <Route path={`${leadRetrievalRegisterPath()}/:eventId/invite/:token`} exact>
        <LeadRetrievalInviteRedeemWrapper />
      </Route>,
      <Route path={`${leadRetrievalRegisterPath()}/:eventId/:subpath?`} exact>
        <LeadRetrievalRegisterWrapper />
      </Route>,
      <Route>
        <Navigation currentUser={currentUser}>
          <RouteErrorBoundary>
            <Switch>
              <ProtectedRoute
                exact
                path={accountPath()}
                authorized={admin}
                redirectTo={eventsPath()}
              >
                <Account currentUser={currentUser} account={currentAccount} />
              </ProtectedRoute>
              <ProtectedRoute
                path={accountPlanPath()}
                authorized={admin}
                redirectTo={eventsPath()}
              >
                <Plan
                  subscription={currentAccount.subscription}
                  showBilling={!!currentAccount.stripeCustomer}
                  onClickResolve={handleClickResolve}
                />
              </ProtectedRoute>
              <ProtectedRoute
                path={accountTeamPath()}
                authorized={admin}
                redirectTo={eventsPath()}
              >
                <TeamMembers
                  currentUser={currentUser}
                  account={currentAccount}
                  accountUsers={accountUsers}
                />
              </ProtectedRoute>
              <ProtectedRoute
                path={upgradePath()}
                authorized={admin}
                redirectTo={eventsPath()}
              >
                <UpgradePlan
                  currentAccount={currentAccount}
                  onHide={() => history.push(accountPlanPath())}
                />
              </ProtectedRoute>
              <Route path={[eventsPath(), eventsFuturePath(), eventsPastPath()]} exact>
                <EventsIndex onClickResolve={handleClickResolve} currentUser={currentUser} />
              </Route>
              <Route path="/events/:eventId">
                <PusherWrapper>
                  <EventShow
                    currentUser={currentUser}
                    accountUsers={accountUsers}
                  />
                </PusherWrapper>
              </Route>
              <Route>
                <Redirect to={eventsPath()} />
              </Route>
            </Switch>
          </RouteErrorBoundary>
        </Navigation>
      </Route>
    </Switch>
  )
}

export default Authenticated
